import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useScenario } from '../context/ScenarioContext';
import { SectionHeader, Callout } from '../components/UI';
import { api, moneyFull } from '../lib/api';
import { RotateCcw, Save, ChevronDown, ChevronRight, Lock } from 'lucide-react';
import { toast } from 'sonner';

// Batch B — smarter Assumptions control room. Every driver now shows:
//   • Baseline value
//   • Basis toggle (% of revenue / flat $ / growth / fixed)
//   • 12-month override grid (blank = inherits baseline)
//   • Manual-input-in-blue finance convention (blue = overrides, black =
//     baseline/derived)
// Also includes a seasonality editor for the revenue index (12 months).

const BASIS_LABELS = {
  fixed: 'Fixed',
  growth: 'Growth %',
  pct_of_revenue: '% of revenue',
  flat_per_month: 'Flat $/mo',
};

// Which basis options are toggleable for a given driver. Not every driver
// makes sense as any basis (e.g. starting revenue must stay fixed).
const BASIS_OPTIONS_FOR = (d) => {
  if (d.key === 'starting_monthly_revenue' || d.key === 'starting_cash') return ['fixed'];
  if (d.key === 'monthly_growth_rate') return ['growth'];
  // General-purpose drivers can flip between % and flat.
  return ['pct_of_revenue', 'flat_per_month'];
};

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatUnit(basis) {
  if (basis === 'pct_of_revenue' || basis === 'growth') return '%';
  if (basis === 'flat_per_month' || basis === 'fixed') return '$';
  return '';
}

function toDisplay(value, basis) {
  if (value == null || value === '') return '';
  if (basis === 'pct_of_revenue' || basis === 'growth') return (Number(value) * 100).toFixed(2);
  return Number(value).toString();
}

function fromDisplay(raw, basis) {
  if (raw === '' || raw == null) return null;
  const num = Number(raw);
  if (Number.isNaN(num)) return null;
  if (basis === 'pct_of_revenue' || basis === 'growth') return num / 100;
  return num;
}

export default function Assumptions() {
  const { active, updateAssumptions } = useScenario();
  const [catalog, setCatalog] = useState(null);
  const [draft, setDraft] = useState({ drivers: {}, seasonality: null });
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState({});   // per-driver row expansion for month grid

  useEffect(() => {
    api.get('/scenarios/driver-catalog').then((r) => setCatalog(r.data));
  }, []);

  const reloadDraft = useCallback(() => {
    if (!active) return;
    const a = active.assumptions || {};
    setDraft({
      drivers: JSON.parse(JSON.stringify(a.drivers || {})),
      seasonality: JSON.parse(JSON.stringify(a.seasonality || { revenue_index: {}, mode: 'constrained_sum' })),
    });
  }, [active]);

  useEffect(() => { reloadDraft(); }, [reloadDraft]);

  const sections = useMemo(() => {
    if (!catalog) return {};
    const grouped = {};
    catalog.drivers.forEach((d) => { (grouped[d.section] = grouped[d.section] || []).push(d); });
    return grouped;
  }, [catalog]);

  const dirty = useMemo(() => {
    if (!active) return false;
    return JSON.stringify(draft) !== JSON.stringify({
      drivers: active.assumptions?.drivers || {},
      seasonality: active.assumptions?.seasonality || null,
    });
  }, [draft, active]);

  const seasonSum = useMemo(() => {
    if (!draft.seasonality?.revenue_index) return 12;
    return Object.values(draft.seasonality.revenue_index).reduce((a, b) => a + Number(b || 0), 0);
  }, [draft.seasonality]);

  if (!active || !catalog) return <div className="label-editorial">Loading…</div>;
  const isBudget = active.kind === 'budget';

  const activeDriver = (key) => draft.drivers[key] || {};
  const activeAssumptionDriver = (key) => (active.assumptions?.drivers || {})[key] || {};

  // --- Field mutators ---------------------------------------------------------
  const setDriverBaseline = (key, raw) => {
    const basis = activeDriver(key).basis || catalog.drivers.find((d) => d.key === key)?.basis;
    const v = fromDisplay(raw, basis);
    setDraft((prev) => ({
      ...prev,
      drivers: { ...prev.drivers, [key]: { ...prev.drivers[key], baseline: v } },
    }));
  };
  const setDriverBasis = (key, basis) => {
    setDraft((prev) => ({
      ...prev,
      drivers: { ...prev.drivers, [key]: { ...prev.drivers[key], basis } },
    }));
  };
  const setDriverOverride = (key, month, raw) => {
    const basis = activeDriver(key).basis || catalog.drivers.find((d) => d.key === key)?.basis;
    const v = fromDisplay(raw, basis);
    setDraft((prev) => {
      const overrides = { ...(prev.drivers[key]?.monthly_overrides || {}) };
      if (v == null) delete overrides[String(month)];
      else overrides[String(month)] = v;
      return {
        ...prev,
        drivers: { ...prev.drivers, [key]: { ...prev.drivers[key], monthly_overrides: overrides } },
      };
    });
  };
  const clearOverride = (key, month) => setDriverOverride(key, month, '');
  const setSeasonMonth = (month, raw) => {
    const v = Number(raw);
    if (Number.isNaN(v)) return;
    setDraft((prev) => ({
      ...prev,
      seasonality: {
        ...prev.seasonality,
        revenue_index: { ...prev.seasonality.revenue_index, [String(month)]: v },
      },
    }));
  };
  const resetSeasonToUniform = () => {
    const uniform = {};
    for (let m = 1; m <= 12; m += 1) uniform[String(m)] = 1.0;
    setDraft((prev) => ({ ...prev, seasonality: { ...prev.seasonality, revenue_index: uniform } }));
  };

  // --- Save ------------------------------------------------------------------
  const save = async () => {
    if (isBudget) {
      toast.error('This scenario is a frozen budget — duplicate it to edit.');
      return;
    }
    setSaving(true);
    try {
      await updateAssumptions(active.id, {
        drivers: draft.drivers,
        seasonality: draft.seasonality,
      });
      toast.success('Assumptions saved.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed.');
    } finally { setSaving(false); }
  };
  const restore = () => { reloadDraft(); toast('Restored to saved values.'); };

  // --- Renders ---------------------------------------------------------------
  const renderMonthGrid = (d) => {
    const key = d.key;
    const drv = activeDriver(key);
    const basis = drv.basis || d.basis;
    const overrides = drv.monthly_overrides || {};
    return (
      <div className="mt-2 grid grid-cols-6 md:grid-cols-12 gap-1.5 bg-indie-cream/40 p-3 border border-indie-navy/10">
        {MONTH_LABELS.map((label, i) => {
          const month = i + 1;
          const overridden = overrides[String(month)] != null;
          const val = overridden ? overrides[String(month)] : null;
          return (
            <div key={month} className="flex flex-col">
              <div className="text-[9px] uppercase tracking-wider text-indie-ink/50 mb-0.5 text-center">{label}</div>
              <input
                type="number"
                step="0.01"
                value={val == null ? '' : toDisplay(val, basis)}
                placeholder={drv.baseline != null ? toDisplay(drv.baseline, basis) : '—'}
                onChange={(e) => setDriverOverride(key, month, e.target.value)}
                data-testid={`override-${key}-${month}`}
                className={`w-full text-xs px-1.5 py-1 text-right num-tabular border focus:outline-none focus:border-indie-navy ${
                  overridden
                    ? 'border-indie-cobalt bg-indie-cobalt/[0.08] text-indie-cobalt font-semibold'    // manual = blue (finance convention)
                    : 'border-indie-navy/10 bg-white text-indie-ink/40 italic'                        // inherits = grey
                }`}
                title={overridden ? 'Override — click to edit, blank to inherit baseline' : 'Baseline value (inherited)'}
              />
              {overridden && (
                <button
                  type="button"
                  onClick={() => clearOverride(key, month)}
                  className="text-[9px] text-indie-ink/40 hover:text-indie-neg-dark uppercase tracking-wider mt-0.5"
                  data-testid={`clear-override-${key}-${month}`}
                >clear</button>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-10">
      <SectionHeader
        eyebrow={`Plan Inputs${isBudget ? ' · Budget snapshot (frozen)' : ''}`}
        title="The plan drivers."
        description={
          isBudget
            ? 'This is a frozen budget snapshot — read-only. Contact an admin if the Keystone plan needs to be reopened.'
            : 'Every driver has a baseline value AND per-month overrides. Blue cells = manual overrides. Grey/italic cells = inherited baseline. Toggle each driver’s basis (% of revenue vs flat $/mo) as needed. Save to update every downstream chart.'
        }
        right={
          <div className="flex gap-3">
            <button data-testid="restore-btn" onClick={restore} disabled={!dirty || isBudget} className="flex items-center gap-2 border-2 border-indie-navy text-indie-navy px-4 py-2 hover:bg-indie-navy hover:text-indie-cream disabled:opacity-40">
              <RotateCcw size={15} /> Restore
            </button>
            <button data-testid="save-assumptions-btn" onClick={save} disabled={!dirty || saving || isBudget} className="flex items-center gap-2 bg-indie-yellow text-indie-ink font-semibold px-4 py-2.5 hover:bg-indie-yellow/85 disabled:opacity-50">
              <Save size={15} /> {isBudget ? 'Frozen' : saving ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
            </button>
          </div>
        }
      />

      {isBudget && (
        <Callout title="This is a frozen budget" tone="warn">
          <div className="flex items-center gap-2">
            <Lock size={14} /> Any changes must be made on a forecast scenario. Freeze another one when you’re ready.
          </div>
        </Callout>
      )}

      {/* Seasonality editor — revenue only, 12 months. */}
      <div className="bg-white border-t-4 border-indie-navy">
        <div className="bg-indie-navy text-indie-cream px-6 py-3 flex items-center justify-between">
          <div>
            <div className="label-editorial-cream">Revenue seasonality</div>
            <div className="text-[11px] text-indie-cream/60 mt-0.5">
              Monthly weight applied to underlying revenue. Sum should stay near 12 (mode: {draft.seasonality?.mode || 'constrained_sum'}) — annual total stays anchored.
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className={`text-xs num-tabular ${Math.abs(seasonSum - 12) < 0.01 ? 'text-indie-pos' : 'text-indie-yellow'}`}>
              sum: {seasonSum.toFixed(2)} / 12
            </div>
            <button
              type="button"
              onClick={resetSeasonToUniform}
              disabled={isBudget}
              data-testid="season-reset-uniform"
              className="text-[11px] uppercase tracking-wider border border-indie-cream/40 text-indie-cream px-2 py-1 hover:bg-indie-cream/10 disabled:opacity-40"
            >
              Reset to flat (1.0×)
            </button>
          </div>
        </div>
        <div className="grid grid-cols-6 md:grid-cols-12 gap-1.5 p-4">
          {MONTH_LABELS.map((label, i) => {
            const m = i + 1;
            const v = draft.seasonality?.revenue_index?.[String(m)];
            const isDefault = catalog.default_seasonality.revenue_index[String(m)];
            const changed = v != null && Math.abs(v - isDefault) > 0.001;
            return (
              <div key={m} className="flex flex-col">
                <div className="text-[9px] uppercase tracking-wider text-indie-ink/50 mb-0.5 text-center">{label}</div>
                <input
                  type="number"
                  step="0.05"
                  min="0"
                  value={v == null ? '' : Number(v).toFixed(2)}
                  onChange={(e) => setSeasonMonth(m, e.target.value)}
                  data-testid={`season-${m}`}
                  disabled={isBudget}
                  className={`w-full text-xs px-1.5 py-1 text-right num-tabular border focus:outline-none focus:border-indie-navy disabled:opacity-40 ${
                    changed ? 'border-indie-cobalt bg-indie-cobalt/[0.08] text-indie-cobalt font-semibold' : 'border-indie-navy/15 bg-white text-indie-ink'
                  }`}
                />
                <div className="text-[9px] text-indie-ink/40 text-center mt-0.5">×</div>
              </div>
            );
          })}
        </div>
        <div className="px-4 pb-3 text-[11px] text-indie-ink/50 leading-relaxed">
          Blue = you’ve nudged this month away from the default index. Sum stays close to 12 so annual revenue is preserved — a weight of 1.20 in July + 0.70 in December means July grows by that share and December shrinks by that share, holding the year constant.
        </div>
      </div>

      {/* Driver sections */}
      <div className="space-y-8">
        {Object.entries(sections).map(([section, items]) => (
          <div key={section} className="bg-white border-t-4 border-indie-navy">
            <div className="bg-indie-navy text-indie-cream px-6 py-3 flex items-center justify-between">
              <div className="label-editorial-cream">{section}</div>
              <div className="text-xs text-indie-cream/60">{items.length} drivers</div>
            </div>
            <div className="divide-y divide-indie-navy/10">
              {items.map((d, i) => {
                const key = d.key;
                const drv = activeDriver(key);
                const savedDrv = activeAssumptionDriver(key);
                const basis = drv.basis || d.basis;
                const baselineChanged = drv.baseline !== savedDrv.baseline;
                const basisChanged = drv.basis && drv.basis !== savedDrv.basis;
                const overrides = drv.monthly_overrides || {};
                const overrideCount = Object.keys(overrides).length;
                const basisOptions = BASIS_OPTIONS_FOR(d);
                return (
                  <div key={key} className={`px-6 py-4 ${i % 2 === 0 ? 'bg-white' : 'bg-indie-cream/40'}`}>
                    <div className="grid grid-cols-1 md:grid-cols-[1fr_140px_180px_120px] gap-4 items-center">
                      {/* Label + note */}
                      <div>
                        <div className="font-medium text-indie-navy">{d.label}</div>
                        <div className="text-xs text-indie-ink/60 mt-0.5 leading-relaxed">{d.note}</div>
                      </div>

                      {/* Basis toggle */}
                      <select
                        value={basis}
                        onChange={(e) => setDriverBasis(key, e.target.value)}
                        disabled={isBudget || basisOptions.length === 1}
                        data-testid={`basis-${key}`}
                        className={`text-xs px-2 py-1.5 border focus:outline-none focus:border-indie-navy ${
                          basisChanged ? 'border-indie-cobalt bg-indie-cobalt/[0.08] text-indie-cobalt font-semibold' : 'border-indie-navy/20 bg-white text-indie-ink'
                        } disabled:opacity-60`}
                      >
                        {basisOptions.map((b) => (
                          <option key={b} value={b}>{BASIS_LABELS[b]}</option>
                        ))}
                      </select>

                      {/* Baseline input */}
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-indie-ink/50 w-3 text-right">{formatUnit(basis)}</span>
                        <input
                          data-testid={`driver-${key}`}
                          type="number"
                          step="0.01"
                          value={toDisplay(drv.baseline ?? d.baseline, basis)}
                          onChange={(e) => setDriverBaseline(key, e.target.value)}
                          disabled={isBudget}
                          className={`flex-1 bg-white border px-3 py-1.5 num-tabular text-right text-sm focus:outline-none ${
                            baselineChanged ? 'border-indie-cobalt bg-indie-cobalt/[0.08] text-indie-cobalt font-semibold' : 'border-indie-navy/20 text-indie-ink'
                          } disabled:opacity-60`}
                        />
                      </div>

                      {/* Expand → month grid */}
                      <button
                        type="button"
                        onClick={() => setExpanded((p) => ({ ...p, [key]: !p[key] }))}
                        data-testid={`toggle-months-${key}`}
                        className={`inline-flex items-center gap-1 justify-center text-[11px] uppercase tracking-wider font-semibold px-2 py-1.5 border ${
                          overrideCount > 0
                            ? 'border-indie-cobalt text-indie-cobalt bg-indie-cobalt/[0.06]'
                            : 'border-indie-navy/20 text-indie-ink/70 bg-white hover:bg-indie-cream'
                        }`}
                      >
                        {expanded[key] ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                        {overrideCount ? `${overrideCount} override${overrideCount > 1 ? 's' : ''}` : 'Per-month'}
                      </button>
                    </div>

                    {expanded[key] && renderMonthGrid(d)}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="text-[11px] text-indie-ink/50 leading-relaxed max-w-3xl">
        <strong>Finance convention:</strong> blue cells and blue borders indicate manual overrides you’ve entered. Black/grey values are inherited from the baseline (or, on the seasonality grid, from the default index). Formula-derived values (revenue lines, expense sub-totals) appear in the P&L in black. This mirrors the standard model-building rule: <em>hardcoded inputs blue, calculations black.</em>
      </div>
    </div>
  );
}
